import * as HC from '@radix-ui/react-hover-card';
import { useContext, useEffect, useId, useRef, useState, type ReactElement } from 'react';
import type { ChannelPerson } from '@/lib/channelPerson';
import { personDuty, personZone } from '@/lib/channelPerson';
import { useResponseClock } from '@/lib/responseClock';
import { Avatar } from './Avatar';
import { PersonTimeZoneContext } from '@/lib/personTimeZone';
import { formatDateTime } from '@/lib/dataFormat';
import { PropertyList, PropertyRow } from './PropertyList';

/** Shared value rows for the hover card and the lightweight person panel. */
export function PersonProperties({ person, now }: { person: ChannelPerson; now?: number }) {
  const clock = useResponseClock(now);
  const display = useContext(PersonTimeZoneContext);
  const duty = personDuty(person.duty, clock);
  return (
    <PropertyList>
      <PropertyRow label="Учётки">
        {person.accounts.length ? (
          <ul aria-label="Связанные учётки" className="space-y-2">
            {person.accounts.map((account) => (
              <li key={account.id} className="min-w-0">
                <span className="block text-2xs text-fg-muted">{account.channel}</span>
                <span
                  data-slot="person-account-address"
                  title={account.address}
                  className="block max-w-full overflow-x-auto whitespace-nowrap"
                >
                  {account.address}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          'Нет связанных учёток'
        )}
      </PropertyRow>
      <PropertyRow label="Часовой пояс">
        {personZone(person.timeZone, clock, person.timeZoneLabel)}
      </PropertyRow>
      <PropertyRow label="Дежурство">
        <span className={duty.state === 'on' ? 'text-success-fg' : 'text-fg'}>
          {duty.state === 'on' ? 'На дежурстве' : duty.state === 'off' ? 'Не на дежурстве' : 'Неизвестно'}
        </span>
        {duty.stale && <span className="block text-xs text-fg-muted">Данные устарели</span>}
        {duty.checkedAt && (
          <time
            dateTime={duty.checkedAt}
            className="block text-xs text-fg-muted"
            title={formatDateTime(duty.checkedAt, display.timeZone) + ' · ' + display.label}
          >
            Данные на {formatDateTime(duty.checkedAt, display.timeZone)}{' '}
            {display.label === 'Местное'
              ? 'Местное · ' + personZone(display.timeZone, Date.parse(duty.checkedAt))
              : display.label}
          </time>
        )}
      </PropertyRow>
    </PropertyList>
  );
}
export function PersonSummary({ person, now }: { person: ChannelPerson; now?: number }) {
  return (
    <section data-slot="person-summary" aria-label={'Человек: ' + person.name} className="min-w-0 space-y-3">
      <header className="flex min-w-0 items-center gap-3">
        <Avatar name={person.name} src={person.avatar} size="lg" />
        <div className="min-w-0">
          <h3 className="type-subheading break-words">{person.name}</h3>
          <p className="text-xs text-fg-muted">Учётки одного человека</p>
        </div>
      </header>
      <PersonProperties person={person} now={now} />
    </section>
  );
}
/** Keeps the supplied inline link geometry. Focus and Escape work without a pointer. */
export function PersonPeek({ person, children }: { person: ChannelPerson; children: ReactElement }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const trigger = useRef<HTMLElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const cancel = () => {
    clearTimeout(timer.current);
    timer.current = undefined;
  };
  const close = () => {
    cancel();
    setOpen(false);
  };
  const leave = () => {
    if (trigger.current === document.activeElement) {
      cancel();
      return;
    }
    cancel();
    timer.current = setTimeout(() => {
      timer.current = undefined;
      setOpen(false);
    }, 120);
  };
  return (
    <HC.Root open={open} onOpenChange={setOpen} openDelay={0} closeDelay={0}>
      <HC.Trigger
        asChild
        ref={(node) => {
          trigger.current = node;
        }}
        aria-describedby={open ? id : undefined}
        onPointerEnter={(event) => {
          event.preventDefault();
          cancel();
          if (event.pointerType !== 'touch')
            timer.current = setTimeout(() => {
              timer.current = undefined;
              setOpen(true);
            }, 180);
        }}
        onPointerLeave={(event) => {
          event.preventDefault();
          leave();
        }}
        onFocus={(event) => {
          event.preventDefault();
          cancel();
          setOpen(true);
        }}
        onBlur={(event) => {
          event.preventDefault();
          if (!event.currentTarget.matches(':hover')) close();
        }}
        onKeyDown={(event) => {
          if ((open || timer.current !== undefined) && event.key === 'Escape') {
            event.preventDefault();
            close();
          }
        }}
        onClick={close}
      >
        {children}
      </HC.Trigger>
      <HC.Portal>
        <HC.Content
          data-slot="person-peek"
          id={id}
          role="tooltip"
          side="top"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          onPointerEnter={(event) => {
            event.preventDefault();
            cancel();
          }}
          onPointerLeave={(event) => {
            event.preventDefault();
            leave();
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            close();
          }}
          className="z-popover w-80 max-w-full animate-pop-in rounded-lg border border-line bg-elevated p-4 shadow-lg"
        >
          <PersonSummary person={person} />
          <HC.Arrow className="fill-elevated" width={10} height={5} />
        </HC.Content>
      </HC.Portal>
    </HC.Root>
  );
}
